// SearchModal — full-screen overlay with live keyword search.
//
// Architecture:
//   - The trigger button (in Navbar) calls openSearch() to show the modal.
//   - On first open, we fetch the search index JSON (cached after first load).
//   - Each keystroke runs an in-memory scorer and renders the top results.
//   - Keyboard shortcuts: ⌘/Ctrl+K and `/` open the modal; Esc / backdrop
//     click close it; arrow keys navigate results; Enter opens the
//     highlighted result.

import {
  useEffect,
  useRef,
  useState,
  type MouseEvent,
} from 'react';
import { useNavigate } from 'react-router-dom';

import type { SearchEntry, ScoredEntry } from '../types';

// ----- Tuning knobs -----
// Adjust these if relevance starts feeling off. The defaults were picked
// to favour exact-title matches but still surface body text matches when
// there's no better hit.
const MAX_RESULTS = 12;
const SNIPPET_LEN = 140;
const DEBOUNCE_MS = 60;

// Scoring weights — higher numbers = stronger match. The cumulative score
// for an entry is the sum of each rule that fires.
const SCORE_TITLE_EXACT      = 8;   // exact word in heading
const SCORE_TITLE_PREFIX     = 4;   // prefix match in heading
const SCORE_HEADING_EXACT    = 6;
const SCORE_HEADING_PREFIX   = 3;
const SCORE_PAGE_EXACT       = 4;
const SCORE_BODY_HIT_MAX     = 5;   // capped — many body hits can't outrank a title match
const SCORE_ALL_TOKENS_BONUS = 3;   // bonus when every query word matches somewhere

// ----- Module-level event channel -----
// Simple pub/sub so Navbar can call openSearch() without React context.
// Limited surface, no third-party dep.
//
// `prefill` is an optional initial query — used by the home page quick-link
// chips so clicking "Mark Attendance" opens the modal with that text
// already typed in.
type OpenHandler = (prefill?: string) => void;
let openHandler: OpenHandler | null = null;

/** Open the search modal. Safe to call before <SearchModal> has mounted —
 *  in that case it's a no-op.
 *
 *  @param prefill - optional initial query to populate the input */
export function openSearch(prefill?: string): void {
  if (openHandler) openHandler(prefill);
}

// ===================================================================
// Helpers
// ===================================================================

/** Split a string into lowercase alphanumeric word tokens. */
function tokenize(s: string): string[] {
  return (s ?? '').toLowerCase().match(/[a-z0-9]+/g) ?? [];
}

/** Escape HTML special chars before inserting into innerHTML-rendered snippets. */
function escapeHtml(s: string): string {
  const map: Record<string, string> = {
    '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;',
  };
  return String(s).replace(/[&<>"']/g, (c) => map[c]);
}

/** Wrap query matches in <mark> for highlight. Sorts longest-first so
 *  "attendance" doesn't get broken up by also matching "att". */
function highlight(text: string, tokens: string[]): string {
  const escaped = escapeHtml(text);
  if (tokens.length === 0) return escaped;
  const sorted = [...tokens].sort((a, b) => b.length - a.length);
  const pattern = sorted
    .map((t) => t.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'))
    .join('|');
  const re = new RegExp(`(${pattern})`, 'gi');
  return escaped.replace(re, '<mark>$1</mark>');
}

/** Score a single index entry against the tokenised query.
 *  Returns 0 if no match — the caller filters these out. */
function scoreEntry(entry: SearchEntry, queryTokens: string[]): number {
  if (queryTokens.length === 0) return 0;

  const titleTokens   = tokenize(entry.title);
  const headingTokens = tokenize(entry.heading);
  const bodyTokens    = tokenize(entry.content);
  const pageTokens    = tokenize(entry.pageName);

  let score = 0;
  let allMatched = true;

  for (const q of queryTokens) {
    let foundForThisToken = false;

    // Exact-word matches in higher-importance fields.
    if (titleTokens.includes(q))   { score += SCORE_TITLE_EXACT;   foundForThisToken = true; }
    if (headingTokens.includes(q)) { score += SCORE_HEADING_EXACT; foundForThisToken = true; }
    if (pageTokens.includes(q))    { score += SCORE_PAGE_EXACT;    foundForThisToken = true; }

    // Prefix matches (e.g. "atten" should match "attendance").
    // We only score the first prefix hit per field to avoid duplication.
    if (titleTokens.some((t) => t.length > q.length && t.startsWith(q))) {
      score += SCORE_TITLE_PREFIX;
      foundForThisToken = true;
    }
    if (headingTokens.some((t) => t.length > q.length && t.startsWith(q))) {
      score += SCORE_HEADING_PREFIX;
      foundForThisToken = true;
    }

    // Body hits — count up to a cap so they can't single-handedly outrank
    // a clean title match. A 100-hit body match shouldn't beat a 1-hit title.
    let bodyHits = 0;
    for (const bt of bodyTokens) {
      if (bt === q || (bt.length > q.length && bt.startsWith(q))) bodyHits++;
    }
    if (bodyHits > 0) {
      score += Math.min(bodyHits, SCORE_BODY_HIT_MAX);
      foundForThisToken = true;
    }

    if (!foundForThisToken) allMatched = false;
  }

  // Reward results where every query token matched something. This rules
  // out spurious one-word matches when the user typed three.
  if (allMatched && queryTokens.length > 1) score += SCORE_ALL_TOKENS_BONUS;

  return score;
}

/** Build a snippet around the first query-token match in the body. */
function makeSnippet(body: string, queryTokens: string[]): string {
  if (!body) return '';
  const lower = body.toLowerCase();
  let bestPos = -1;
  for (const t of queryTokens) {
    const p = lower.indexOf(t);
    if (p !== -1 && (bestPos === -1 || p < bestPos)) bestPos = p;
  }

  let start = bestPos > 60 ? bestPos - 60 : 0;
  // Slide back to a word boundary so we don't cut a word in half.
  while (start > 0 && body[start] !== ' ' && start > bestPos - 80) start--;

  let snippet = body.substr(start, SNIPPET_LEN);
  if (start > 0) snippet = '…' + snippet;
  if (start + SNIPPET_LEN < body.length) snippet = snippet + '…';

  return highlight(snippet, queryTokens);
}

/** Map "teacher-guide.html" → "/teacher" (the React route).
 *  The pre-built index references HTML filenames from the static version of
 *  this site; we rewrite them on the fly to point at React Router paths. */
function urlToRoute(url: string): string {
  const [pageFile, anchor] = url.split('#');
  const slug = pageFile.replace(/-guide\.html$/, '').replace(/\.html$/, '');
  const path = slug === 'index' ? '/' : `/${slug}`;
  return anchor ? `${path}#${anchor}` : path;
}

// ===================================================================
// Component
// ===================================================================

export default function SearchModal() {
  const [open, setOpen] = useState<boolean>(false);
  const [query, setQuery] = useState<string>('');
  const [results, setResults] = useState<ScoredEntry[]>([]);
  const [statusText, setStatusText] = useState<string>(
    'Type to search across all guides.'
  );

  // The fetched index — cached after first load. Stored in a ref so we
  // don't trigger re-renders just because we filled the cache.
  const indexRef = useRef<SearchEntry[] | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);

  const navigate = useNavigate();

  // ----- Register openSearch() handler -----
  // Accepts an optional `prefill` query so the home page can open the modal
  // with text already typed in (used by the popular-search chips).
  useEffect(() => {
    openHandler = (prefill?: string) => {
      if (prefill !== undefined) setQuery(prefill);
      setOpen(true);
    };
    return () => { openHandler = null; };
  }, []);

  // ----- Global keyboard shortcuts (⌘K / Ctrl+K / `/`) -----
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      const target = document.activeElement;
      const tagName = target?.tagName;
      const isTextInput = tagName === 'INPUT' || tagName === 'TEXTAREA';

      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setOpen(true);
      } else if (e.key === '/' && !isTextInput) {
        e.preventDefault();
        setOpen(true);
      } else if (e.key === 'Escape' && open) {
        setOpen(false);
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  // ----- Search execution (defined before useEffect that uses it) -----
  function runSearch(q: string): void {
    if (!indexRef.current) return;
    const trimmed = q.trim();

    if (!trimmed) {
      setResults([]);
      setStatusText('Type to search across all guides.');
      return;
    }

    const tokens = tokenize(trimmed);
    const scored: ScoredEntry[] = [];
    for (const entry of indexRef.current) {
      const s = scoreEntry(entry, tokens);
      if (s > 0) scored.push({ entry, score: s });
    }
    scored.sort((a, b) => b.score - a.score);
    const top = scored.slice(0, MAX_RESULTS);

    setResults(top);
    setStatusText(
      top.length === 0
        ? `No results for "${trimmed}". Try different keywords.`
        : `${top.length} result${top.length === 1 ? '' : 's'}`
    );
  }

  // ----- Load the index when the modal first opens -----
  useEffect(() => {
    if (!open || indexRef.current) return;
    // Use BASE_URL so it works under sub-path deploys (e.g. /help/).
    const base = import.meta.env.BASE_URL ?? '/';
    fetch(`${base}search-index.json`)
      .then((r) => r.json() as Promise<SearchEntry[]>)
      .then((data) => {
        indexRef.current = data;
        // Refresh results in case the user already typed something.
        runSearch(query);
      })
      .catch((err: unknown) => {
        console.error('[search] Failed to load index:', err);
        setStatusText('Could not load the search index. Please refresh.');
      });
    // We intentionally exclude `query` and `runSearch` from deps — we only
    // want to fetch once when the modal opens, not on every keystroke.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // ----- Autofocus the input on open -----
  useEffect(() => {
    if (open && inputRef.current) {
      // Delay slightly so the input is actually mounted/visible.
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [open]);

  // ----- Debounced search execution on query change -----
  useEffect(() => {
    if (!open) return;
    const handle = window.setTimeout(() => runSearch(query), DEBOUNCE_MS);
    return () => window.clearTimeout(handle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query, open]);

  // ----- Result click handler -----
  // Closes the modal and navigates via React Router so we don't do a full
  // page reload (which would dump component state and refetch the index).
  function onResultClick(e: MouseEvent<HTMLAnchorElement>, url: string): void {
    e.preventDefault();
    setOpen(false);
    navigate(urlToRoute(url));
  }

  if (!open) return null;

  const tokens = tokenize(query);

  return (
    <div
      className="search-modal"
      role="dialog"
      aria-modal="true"
      aria-label="Search the knowledge base"
    >
      <div className="search-modal__backdrop" onClick={() => setOpen(false)} />

      <div className="search-modal__panel">
        <div className="search-modal__box">
          <span className="search-modal__icon" aria-hidden="true">🔍</span>
          <input
            ref={inputRef}
            type="search"
            className="search-modal__input"
            placeholder="Search guides, features, FAQ…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            autoComplete="off"
            spellCheck="false"
          />
          <button
            className="search-modal__close"
            aria-label="Close"
            onClick={() => setOpen(false)}
            type="button"
          >×</button>
        </div>

        <div className="search-modal__status">{statusText}</div>

        <div className="search-modal__results" role="listbox">
          {results.map(({ entry }, i) => (
            <a
              key={`${entry.url}-${i}`}
              className="search-result"
              href={urlToRoute(entry.url)}
              onClick={(e) => onResultClick(e, entry.url)}
            >
              <div className="search-result__page">{entry.pageName}</div>
              <div
                className="search-result__title"
                // Highlighting requires raw HTML. The source is our own JSON
                // index, escaped via escapeHtml() in highlight(), so this is
                // safe — but if you ever change the index source, audit
                // this carefully.
                dangerouslySetInnerHTML={{
                  __html: highlight(entry.heading || entry.title, tokens),
                }}
              />
              {entry.content && (
                <div
                  className="search-result__snippet"
                  dangerouslySetInnerHTML={{
                    __html: makeSnippet(entry.content, tokens),
                  }}
                />
              )}
            </a>
          ))}
        </div>

        <div className="search-modal__footer">
          <span><kbd>↵</kbd> open</span>
          <span><kbd>esc</kbd> close</span>
          <span>Powered by My-Eduate Knowledge Base</span>
        </div>
      </div>
    </div>
  );
}
